import CookieStorageService from '@root/core/src/services/cookie-storage-service';
import MediaAlphaResell from '@root/core/src/components/media-alpha-resell';
import MediaAlphaScriptService from '@root/core/src/services/media-alpha-script-service';
import ProfileParams from '@root/auto-pricing/src/models/profile-params';
import PropTypes from '@root/vendor/prop-types';
import QuotesContext from '@root/quotes/src/models/quotes-context';
import SceneContent from '@root/core/src/components/scene-content';
import SceneWrapper from '@root/core/src/components/scene-wrapper';
import Separator from '@root/core/src/components/separator';
import dayjs from '@root/vendor/dayjs';
import useAnalytics from '@root/core/src/hooks/use-analytics';
import { Fragment, useEffect, useState } from '@root/vendor/react';
import { StyleSheet, Theme } from '@root/core/src/utils/styles';
import { isMobileBrowser } from '@root/core/src/utils/detect-mobile-browser';

export const UNDERWRITING_PENDING_VIEWED_COOKIE = 'underwriting_pending.viewed';

export default function UnderwritingPendingLayout({
  quotesContext,
  profileParams,
  analyticsContext,
}) {
  useAnalytics(analyticsContext);

  const estimatedCompleteAt = quotesContext?.underwritingDecision.estimatedCompleteAt;
  const estimatedCompletionDateString = estimatedCompleteAt && dayjs(estimatedCompleteAt).format('MMMM D');

  const [reviewInProgress, setReviewInProgress] = useState(false);

  useEffect(() => {
    const isFirstView = !CookieStorageService.get(UNDERWRITING_PENDING_VIEWED_COOKIE);

    if (isFirstView) {
      CookieStorageService.set(UNDERWRITING_PENDING_VIEWED_COOKIE, true);
    } else {
      setReviewInProgress(true);
    }
  }, [setReviewInProgress]);

  const heading = reviewInProgress ? 'Review in progress' : 'We need to review your application';

  const mediaAlphaComponent = (
    <MediaAlphaResell
      analyticsContext={analyticsContext}
      explainerText={'Explore these other carriers while we review your application. We’ll share some details to help personalize your quotes, but not your phone number or email. They can only contact you if you choose to share that information on their site.'}
      heading={null}
      placementId={MediaAlphaScriptService.getSoftStopPlacementId(isMobileBrowser())}
      request={MediaAlphaScriptService.buildRequestFromProfile(profileParams.serializeForSubmission())}
      shouldTrackViewEvent={false}
      shouldUseFlushPositioning={true}
    />
  );

  return (
    <SceneWrapper>
      <SceneContent
        positionRelative
        colSizes={{
          lg: 8,
          md: 10,
          xl: 6,
        }}
      >
        <h1 css={styles.heading}>
          {heading}
        </h1>
        <p css={styles.body}>
          {'Our team needs to take a closer look at the information in your application.'}
          {estimatedCompletionDateString && (
            <Fragment>
              {' You can expect the review to be completed by '}
              <strong css={styles.bodyEmphasis}>
                {estimatedCompletionDateString}
              </strong>
              {'.'}
            </Fragment>
          )}
          {' Keep an eye out for an email today with more details about next steps.'}
        </p>
        <Separator css={styles.divider} />
        <h2 css={styles.resellHeading}>
          {'Need coverage sooner?'}
        </h2>
        {mediaAlphaComponent}
      </SceneContent>
    </SceneWrapper>
  );
}

UnderwritingPendingLayout.propTypes = {
  analyticsContext: PropTypes.string.isRequired,
  profileParams: PropTypes.instanceOf(ProfileParams).isRequired,
  quotesContext: PropTypes.instanceOf(QuotesContext).isRequired,
};

const styles = StyleSheet.create({
  heading: {
    ...Theme.heading1(),
    marginTop: 40,
    textAlign: 'center',
  },
  body: {
    ...Theme.paragraph1(),
    margin: '24px 0',
  },
  bodyEmphasis: {
    ...Theme.paragraph1({ bold: true }),
  },
  divider: {
    margin: '16px 0 32px',
  },
  resellHeading: {
    ...Theme.heading3(),
    textAlign: 'center',
  },
});
